/* ==========================================================================
   Hero banner full width (copy of hero-banner-products.js for
   sections/hero-banner-full.liquid, bound to [data-sg-hero-full])

   Only difference: the product row always behaves like mobile (one scrollable
   row with arrows that scrolls to the active slide's products), on desktop too.
   ==========================================================================
   Original notes:
   Hero banner + products

   Keeps the product row in step with the banner slider. Every product carries
   a data-sg-group index pointing at the banner slide it belongs to.

   Desktop: only the active group is in the grid; the rest are display:none.
   Mobile:  every product stays in one scrollable row and the row scrolls to
            the active group's first product when the banner changes.

   The product row is a native scroll-snap container rather than a second
   Swiper — it has to be a CSS grid on desktop and a scroller on mobile, and a
   swiper element owns its children through a shadow-DOM slot, which makes that
   swap fragile.
   ========================================================================== */

(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 990px)';
  // While something is still in progress, re-check this often instead of
  // resuming, and give up waiting after this many tries.
  var RECHECK_MS = 500;
  var MAX_RECHECKS = 120;

  function HeroBannerProducts(root) {
    this.root = root;
    this.banner = root.querySelector('[data-sg-banner]');
    this.track = root.querySelector('[data-sg-track]');
    this.productsArea = root.querySelector('.sg-hero__products');
    this.prev = root.querySelector('[data-sg-prev]');
    this.next = root.querySelector('[data-sg-next]');
    this.products = Array.prototype.slice.call(root.querySelectorAll('[data-sg-group]'));
    this.mq = window.matchMedia(DESKTOP_QUERY);
    this.activeGroup = 0;

    this.pauseEnabled = root.getAttribute('data-sg-pause') === 'true';
    this.resumeDelay = parseInt(root.getAttribute('data-sg-resume-delay'), 10) || 4000;
    this.addDelay = parseInt(root.getAttribute('data-sg-add-delay'), 10) || 20000;
    this.pointerInside = false;
    this.resumeTimer = null;
    this.rechecks = 0;
    this.destroyed = false;
    this.fitFrame = null;
    this.fitConfirmFrame = null;
    this.lastFitHeight = null;
    this.lastFitProductsWidth = null;
    this.onBannerChange = this.onBannerChange.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onPrevClick = this.step.bind(this, -1);
    this.onNextClick = this.step.bind(this, 1);

    // Fit-to-viewport runs even for a banner-only section, so it sits above
    // the products bail-out below.
    this.fitEnabled = root.hasAttribute('data-sg-fit');
    if (this.fitEnabled) {
      this.measureFit = this.measureFit.bind(this);
      this.onFitResize = this.onFitResize.bind(this);
      window.addEventListener('resize', this.onFitResize);
      this.measureFit();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.onFitResize);
      } else {
        window.addEventListener('load', this.onFitResize, { once: true });
      }
    }

    // Light photo slides switch the clear header to black text (see hero-banner-full.css). Also above the
    // products bail-out; watches the slides' active class, since swiper events miss some slide changes.
    if (this.banner) {
      this.syncTone = this.syncTone.bind(this);
      this.toneObserver = new MutationObserver(this.syncTone);
      this.toneObserver.observe(this.banner, { attributes: true, attributeFilter: ['class'], subtree: true });
      this.syncTone();
    }

    if (!this.track || !this.products.length) return;

    this.groups = this.products.map(function (el) {
      return parseInt(el.getAttribute('data-sg-group'), 10) || 0;
    });

    if (this.banner) {
      // swiper-element re-dispatches swiper events on the host with an
      // eventsPrefix of "swiper".
      this.banner.addEventListener('swiperslidechange', this.onBannerChange);
      this.banner.addEventListener('swiperinit', this.onBannerChange);
    }

    if (this.prev) {
      this.prev.addEventListener('click', this.onPrevClick);
    }
    if (this.next) {
      this.next.addEventListener('click', this.onNextClick);
    }

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.pauseEnabled && this.productsArea) {
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onProductPointerDown = this.onProductPointerDown.bind(this);
      this.onProductClick = this.onProductClick.bind(this);
      this.tryResume = this.tryResume.bind(this);

      this.productsArea.addEventListener('pointerenter', this.onPointerEnter);
      this.productsArea.addEventListener('pointerleave', this.onPointerLeave);
      this.productsArea.addEventListener('pointerdown', this.onProductPointerDown, { passive: true });
      this.productsArea.addEventListener('click', this.onProductClick);
      this.productsArea.addEventListener('focusin', this.onPointerEnter);
      this.productsArea.addEventListener('focusout', this.onPointerLeave);
    }

    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', this.onBreakpoint);
    } else if (typeof this.mq.addListener === 'function') {
      this.mq.addListener(this.onBreakpoint);
    }

    this.apply(false);
    this.updateArrows();
  }

  /* ---- Autoplay hold -----------------------------------------------------
     The banner rotating mid-purchase swaps the product set out from under the
     shopper, so any interaction with the products stops it, and it only starts
     again once the pointer has left, no quick-view dialog is open, and the
     grace period has passed. Only ever touches autoplay the merchant turned on.
     ---------------------------------------------------------------------- */

  HeroBannerProducts.prototype.canControlAutoplay = function () {
    return Boolean(
      this.pauseEnabled &&
        this.banner &&
        this.banner.hasAttribute('autoplay') &&
        this.banner.swiper &&
        this.banner.swiper.autoplay
    );
  };

  HeroBannerProducts.prototype.holdAutoplay = function () {
    if (!this.canControlAutoplay()) return;
    window.clearTimeout(this.resumeTimer);
    this.rechecks = 0;
    this.banner.swiper.autoplay.stop();
  };

  HeroBannerProducts.prototype.scheduleResume = function (delay) {
    if (!this.canControlAutoplay()) return;
    window.clearTimeout(this.resumeTimer);
    this.rechecks = 0;
    this.resumeTimer = window.setTimeout(this.tryResume, delay);
  };

  HeroBannerProducts.prototype.isBusy = function () {
    // A quick-view dialog belonging to this section is still open.
    return this.pointerInside || Boolean(this.root.querySelector('dialog[open]'));
  };

  HeroBannerProducts.prototype.tryResume = function () {
    if (!this.canControlAutoplay()) return;
    if (this.isBusy() && this.rechecks < MAX_RECHECKS) {
      this.rechecks += 1;
      this.resumeTimer = window.setTimeout(this.tryResume, RECHECK_MS);
      return;
    }
    this.rechecks = 0;
    this.banner.swiper.autoplay.start();
  };

  /* Hover is a mouse-only signal — a tap would set it and never clear it. */
  HeroBannerProducts.prototype.onPointerEnter = function (event) {
    if (event && event.pointerType && event.pointerType !== 'mouse') return;
    this.pointerInside = true;
    this.holdAutoplay();
  };

  HeroBannerProducts.prototype.onPointerLeave = function (event) {
    if (event && event.pointerType && event.pointerType !== 'mouse') return;
    this.pointerInside = false;
    this.scheduleResume(this.resumeDelay);
  };

  /* Covers taps and drags, which never fire pointerenter on touch. */
  HeroBannerProducts.prototype.onProductPointerDown = function () {
    this.holdAutoplay();
    this.scheduleResume(this.resumeDelay);
  };

  HeroBannerProducts.prototype.onProductClick = function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('[data-popup-open], .advanced-quick-view, .quick-add button, .quick-add a')) return;
    this.holdAutoplay();
    this.scheduleResume(this.addDelay);
  };

  /* ---- Fit to viewport ---------------------------------------------------
     With the section's fit setting on, the grid gets the height left between
     its top edge and the fold: viewport minus everything above the grid
     (header, announcement bar, section padding) minus the section's own
     bottom padding. Document offsets, not viewport ones, so the measurement
     holds wherever the page happens to be scrolled when it runs. The 460px
     floor matches the min-height in hero-banner-products.css — below it the
     section grows past the fold rather than crushing the cards.
     Desktop only; mobile keeps its natural height. */

  HeroBannerProducts.prototype.measureFit = function (secondPass) {
    if (!this.fitEnabled) return;
    if (!this.isDesktop()) {
      this.root.style.removeProperty('--sg-fit-height');
      this.root.style.removeProperty('--sg-fit-products-w');
      this.lastFitHeight = null;
      this.lastFitProductsWidth = null;
      return;
    }
    var grid = this.root.querySelector('.sg-hero__grid');
    if (!grid) return;
    var top = grid.getBoundingClientRect().top + window.scrollY;
    var rootStyles = getComputedStyle(this.root);
    var padBottom = parseFloat(rootStyles.paddingBottom) || 0;
    var height = window.innerHeight - top - padBottom;
    if (height < 460) height = 460;
    height = Math.round(height);
    if (height !== this.lastFitHeight) {
      this.root.style.setProperty('--sg-fit-height', height + 'px');
      this.lastFitHeight = height;
    }

    // Square tiles, collection-grid style: the products column takes exactly
    // the width that makes each media box 1:1 at the row height the fit
    // leaves, and the banner absorbs the leftover width instead of the tiles
    // stretching landscape.
    var rows = parseInt(rootStyles.getPropertyValue('--sg-rows'), 10) || 2;
    var cols = parseInt(rootStyles.getPropertyValue('--sg-cols'), 10) || 2;
    var gap = parseFloat(rootStyles.getPropertyValue('--sg-gap')) || 8;

    var infoH = 0;
    var infos = this.root.querySelectorAll(
      '.sg-hero__product:not(.sg-hero__product--hidden) .card > .card__content'
    );
    Array.prototype.forEach.call(infos, function (el) {
      if (el.offsetHeight > infoH) infoH = el.offsetHeight;
    });
    if (!infoH) infoH = 80;

    var rowH = (height - (rows - 1) * gap) / rows;
    var tileH = rowH - infoH;
    if (tileH < 170) tileH = 170;

    // The row spans the full content width like every other section: the
    // banner takes its capped share (--sg-fit-banner-max × height, from the
    // CSS column definition) and the tiles absorb ALL the remaining width.
    // Tiles may run up to 18% wider than tall — on white packshots cover
    // makes that invisible — before the row gives up and centers instead.
    var ratio = parseFloat(rootStyles.getPropertyValue('--sg-fit-banner-max')) || 1;
    var bannerW = Math.min(height * ratio, grid.clientWidth * 0.65);
    var productsW = grid.clientWidth - bannerW - gap;
    var colW = (productsW - (cols - 1) * gap) / cols;
    if (colW > tileH * 1.18) colW = tileH * 1.18;
    if (colW < tileH * 0.85) colW = tileH * 0.85;
    productsW = cols * colW + (cols - 1) * gap;
    productsW = Math.round(productsW);
    if (productsW !== this.lastFitProductsWidth) {
      this.root.style.setProperty('--sg-fit-products-w', productsW + 'px');
      this.lastFitProductsWidth = productsW;
    }

    // The new width can rewrap titles and change the info height, so run one
    // confirming pass on the next frame.
    if (!secondPass) {
      var self = this;
      window.cancelAnimationFrame(this.fitConfirmFrame);
      this.fitConfirmFrame = window.requestAnimationFrame(function () {
        self.fitConfirmFrame = null;
        self.measureFit(true);
      });
    }
  };

  HeroBannerProducts.prototype.onFitResize = function () {
    if (this.destroyed || this.fitFrame !== null) return;
    var self = this;
    this.fitFrame = window.requestAnimationFrame(function () {
      self.fitFrame = null;
      self.measureFit();
    });
  };

  HeroBannerProducts.prototype.syncTone = function () {
    var self = this;
    if (this.toneFrame) return;
    this.toneFrame = window.requestAnimationFrame(function () {
      self.toneFrame = null;
      var slide = self.banner && self.banner.querySelector('.sg-hero__slide.swiper-slide-active');
      if (slide) self.root.classList.toggle('sg-hero--light-active', slide.classList.contains('sg-hero__slide--light'));
    });
  };

  HeroBannerProducts.prototype.onBannerChange = function () {
    var swiper = this.banner && this.banner.swiper;
    var next = swiper ? swiper.realIndex : 0;
    if (next === this.activeGroup) return;
    this.activeGroup = next;
    this.apply(true);
  };

  HeroBannerProducts.prototype.onBreakpoint = function () {
    this.apply(false);
    this.onFitResize();
  };

  // The full-width section keeps the mobile product row on every screen size.
  HeroBannerProducts.prototype.isDesktop = function () {
    return false;
  };

  /* Show the active group (desktop) or scroll to it (mobile). */
  HeroBannerProducts.prototype.apply = function (animate) {
    var desktop = this.isDesktop();
    var groups = this.groups;
    var active = this.activeGroup;

    this.products.forEach(function (el, i) {
      el.classList.toggle('sg-hero__product--hidden', desktop && groups[i] !== active);
    });

    if (desktop) {
      this.track.scrollLeft = 0;
    } else {
      var first = groups.indexOf(active);
      if (first > -1) this.scrollToIndex(first, animate);
    }

    this.updateArrows();

    // Keep the dimensions established during initial layout. Recalculating
    // the section for every banner transition made the product column move
    // when groups had slightly different title heights.
  };

  HeroBannerProducts.prototype.scrollToIndex = function (index, animate) {
    var target = this.products[index];
    if (!target) return;
    // offsetLeft is measured from the track's border box, while scrollLeft is
    // measured from its content start — without subtracting the left inset,
    // scrolling to the first card would shove it under the padding.
    var style = window.getComputedStyle(this.track);
    var inset = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.borderLeftWidth) || 0);
    var left = target.offsetLeft - this.track.offsetLeft - inset;
    if (left < 0) left = 0;
    if (animate && 'scrollBehavior' in document.documentElement.style) {
      this.track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = left;
    }
  };

  /* One card per arrow click. */
  HeroBannerProducts.prototype.step = function (direction) {
    var card = this.products[0];
    if (!card) return;
    var stride = card.offsetWidth + this.gap();
    var left = this.track.scrollLeft + stride * direction;
    if ('scrollBehavior' in document.documentElement.style) {
      this.track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = left;
    }
  };

  HeroBannerProducts.prototype.gap = function () {
    var value = parseFloat(getComputedStyle(this.track).columnGap);
    return isNaN(value) ? 0 : value;
  };

  HeroBannerProducts.prototype.onScroll = function () {
    if (this.scrollFrame) return;
    var self = this;
    this.scrollFrame = window.requestAnimationFrame(function () {
      self.scrollFrame = null;
      self.updateArrows();
    });
  };

  HeroBannerProducts.prototype.updateArrows = function () {
    if (!this.prev && !this.next) return;

    var track = this.track;
    // 1px of slack absorbs sub-pixel scroll positions.
    var overflowing = !this.isDesktop() && track.scrollWidth - track.clientWidth > 1;
    var atStart = track.scrollLeft <= 1;
    var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;

    if (this.prev) {
      this.prev.classList.toggle('swiper-button-lock', !overflowing);
      this.prev.classList.toggle('swiper-button-disabled', overflowing && atStart);
      this.prev.disabled = !overflowing || atStart;
    }
    if (this.next) {
      this.next.classList.toggle('swiper-button-lock', !overflowing);
      this.next.classList.toggle('swiper-button-disabled', overflowing && atEnd);
      this.next.disabled = !overflowing || atEnd;
    }
  };

  HeroBannerProducts.prototype.destroy = function () {
    this.destroyed = true;
    window.clearTimeout(this.resumeTimer);
    window.cancelAnimationFrame(this.fitFrame);
    window.cancelAnimationFrame(this.fitConfirmFrame);
    window.cancelAnimationFrame(this.scrollFrame);
    window.removeEventListener('resize', this.onFitResize);
    window.removeEventListener('load', this.onFitResize);

    if (this.banner) {
      this.banner.removeEventListener('swiperslidechange', this.onBannerChange);
      this.banner.removeEventListener('swiperinit', this.onBannerChange);
    }
    this.prev?.removeEventListener('click', this.onPrevClick);
    this.next?.removeEventListener('click', this.onNextClick);
    this.track?.removeEventListener('scroll', this.onScroll);

    if (this.productsArea && this.pauseEnabled) {
      this.productsArea.removeEventListener('pointerenter', this.onPointerEnter);
      this.productsArea.removeEventListener('pointerleave', this.onPointerLeave);
      this.productsArea.removeEventListener('pointerdown', this.onProductPointerDown);
      this.productsArea.removeEventListener('click', this.onProductClick);
      this.productsArea.removeEventListener('focusin', this.onPointerEnter);
      this.productsArea.removeEventListener('focusout', this.onPointerLeave);
    }

    if (typeof this.mq.removeEventListener === 'function') {
      this.mq.removeEventListener('change', this.onBreakpoint);
    } else if (typeof this.mq.removeListener === 'function') {
      this.mq.removeListener(this.onBreakpoint);
    }

    if (this.toneObserver) this.toneObserver.disconnect();
    this.root.sgHero = null;
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-sg-hero-full]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.sgHero) return;
      root.sgHero = new HeroBannerProducts(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = [];
    if (event.target.matches && event.target.matches('[data-sg-hero-full]')) {
      roots.push(event.target);
    }
    event.target.querySelectorAll('[data-sg-hero-full]').forEach(function (root) {
      roots.push(root);
    });
    Array.prototype.forEach.call(roots, function (root) {
      if (root.sgHero) root.sgHero.destroy();
    });
  });
})();

/* ---- Announcement bar in step with the banner ------------------------------
   On pages with this section the bar stops its own timer and moves one message
   each time the banner changes slide (so it also holds while the banner holds),
   with its fade stretched to the banner's crossfade: half the transition speed
   out, half in. Pages without this section keep the bar's own rotation. */
(function () {
  function syncAnnouncementBar() {
    var banner = document.querySelector('[data-sg-hero-full] [data-sg-banner]');
    var bar = document.querySelector('slideshow-advanced-component.announcement-bar');
    if (!banner || !bar || !banner.hasAttribute('autoplay')) return;
    if (typeof bar.autoRotateSlides !== 'function' || bar.dataset.sgHeroSync) return;
    // A bar down to one message (e.g. the BFCM sale message shown alone) has nothing to rotate.
    if (bar.querySelectorAll('.slider__slide').length < 2) return;
    bar.dataset.sgHeroSync = 'true';

    var half = Math.round((parseInt(banner.getAttribute('speed'), 10) || 600) / 2);
    bar.pause();
    bar.play = function () {};
    bar.announcerBarAnimationDelay = half;
    bar.style.setProperty('--duration-announcement-bar', half + 'ms');
    banner.addEventListener('swiperslidechangetransitionstart', function () {
      bar.autoRotateSlides();
    });
  }

  customElements.whenDefined('slideshow-advanced-component').then(function () {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncAnnouncementBar);
    } else {
      syncAnnouncementBar();
    }
  });
})();

/* ---- ADD button: hover-only after mouse use --------------------------------
   The cards show ADD on hover, and on keyboard focus for people who Tab. After
   a mouse click on ADD the theme hands focus back to the button when its
   quick-add / cart closes, which the browser counts as :focus-visible, so the
   button stayed up with the pointer gone. Mark the card while that focus came
   from a mouse interaction; the CSS skips the focus reveal for marked cards. */
(function () {
  var pointerLast = false;
  document.addEventListener('pointerdown', function () { pointerLast = true; }, true);
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Tab') pointerLast = false;
  }, true);

  document.addEventListener('focusin', function (event) {
    var card = event.target.closest && event.target.closest('[data-sg-hero-full] .sg-hero__product');
    if (card) card.classList.toggle('sg-hero__product--pointer-focus', pointerLast);
  });

  document.addEventListener('focusout', function (event) {
    var card = event.target.closest && event.target.closest('[data-sg-hero-full] .sg-hero__product');
    if (card && !card.contains(event.relatedTarget)) card.classList.remove('sg-hero__product--pointer-focus');
  });
})();

/* ---- Touch feel (Van 2026-10-21: "a bit moving", "not smooth") -------------
   1. A tap that drifts a few pixels started a drag (Swiper's threshold is 5px),
      which half-faded the banner and then eased back over the full 1.2s
      crossfade. Need 12px of movement before the banner follows the finger.
   2. A finger swipe ran on the same slow 1.2s crossfade as the automatic
      rotation, so the photo lagged behind the gesture. Swipes now finish in
      ~0.45s; the automatic rotation keeps the slider speed setting. */
(function () {
  var TOUCH_THRESHOLD = 12;
  var SWIPE_SPEED = 450;

  function tune(el) {
    var swiper = el.swiper;
    if (!swiper || el.dataset.sgTouchTuned) return Boolean(swiper);
    el.dataset.sgTouchTuned = 'true';
    var baseSpeed = swiper.params.speed;
    swiper.params.threshold = Math.max(swiper.params.threshold || 0, TOUCH_THRESHOLD);
    swiper.on('touchStart', function () {
      swiper.params.speed = Math.min(baseSpeed, SWIPE_SPEED);
    });
    // Swiper starts the snap after its touchEnd event, so restore on the next tick
    swiper.on('touchEnd', function () {
      window.setTimeout(function () { swiper.params.speed = baseSpeed; }, 0);
    });
    return true;
  }

  function tuneAll() {
    var pending = 0;
    document.querySelectorAll('[data-sg-hero-full] swiper-container[data-sg-banner]').forEach(function (el) {
      if (!tune(el)) pending += 1;
    });
    return pending;
  }

  var tries = 0;
  (function wait() {
    if (tuneAll() > 0 && tries++ < 50) window.setTimeout(wait, 100);
  })();
  document.addEventListener('shopify:section:load', function () { tries = 0; window.setTimeout(tuneAll, 0); });
})();

/* Slides after the first keep their photo sources in data-sg-src / data-sg-srcset (hero-banner-full.liquid) until the
   first slide's photo has loaded, so on a slow connection the banner people see first isn't sharing bandwidth with
   the ones they don't see yet (Van 2026-10-22). Also released on a swipe and after 4s at the latest. */
(function () {
  function release(banner) {
    if (banner.dataset.sgHeroReleased) return;
    banner.dataset.sgHeroReleased = 'true';
    banner.querySelectorAll('[data-sg-srcset], [data-sg-src]').forEach(function (el) {
      if (el.hasAttribute('data-sg-srcset')) {
        el.setAttribute('srcset', el.getAttribute('data-sg-srcset'));
        el.removeAttribute('data-sg-srcset');
      }
      if (el.hasAttribute('data-sg-src')) {
        el.setAttribute('src', el.getAttribute('data-sg-src'));
        el.removeAttribute('data-sg-src');
      }
    });
  }

  function watch(banner) {
    if (banner.dataset.sgHeroWatch || !banner.querySelector('[data-sg-srcset], [data-sg-src]')) return;
    banner.dataset.sgHeroWatch = 'true';
    var go = function () { release(banner); };
    var first = banner.querySelector('.sg-hero__slide img:not([data-sg-src])');
    if (!first || (first.complete && first.naturalWidth)) {
      go();
      return;
    }
    first.addEventListener('load', go, { once: true });
    first.addEventListener('error', go, { once: true });
    banner.addEventListener('pointerdown', go, { once: true });
    window.setTimeout(go, 4000);
  }

  function watchAll() {
    document.querySelectorAll('[data-sg-hero-full] .sg-hero__banner').forEach(watch);
  }

  watchAll();
  document.addEventListener('DOMContentLoaded', watchAll);
  document.addEventListener('shopify:section:load', function () { window.setTimeout(watchAll, 0); });
})();

/* The photo settle (hero-banner-full.css) starts with the first slide change, so the photo already showing when the
   slider starts doesn't jump to 104% (Van 2026-10-26: it also made the banner count as painted ~1.7s later). */
(function () {
  function watch(banner) {
    if (banner.dataset.sgHeroMoveWatch) return;
    banner.dataset.sgHeroMoveWatch = 'true';
    banner.addEventListener('swiperslidechangetransitionstart', function () {
      banner.classList.add('sg-hero__slider--moved');
    }, { once: true });
  }

  function watchAll() {
    document.querySelectorAll('[data-sg-hero-full] [data-sg-banner]').forEach(watch);
  }

  watchAll();
  document.addEventListener('DOMContentLoaded', watchAll);
  document.addEventListener('shopify:section:load', function () { window.setTimeout(watchAll, 0); });
})();
